/**
 * Hooks d'authentification.
 *
 * Toute la logique de données vit ici, jamais dans le JSX (docs/24 §3).
 */
import {
  useMutation,
  useQuery,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { api, expectNoContent, unwrap } from "@/shared/api/client";
import type { ApiError } from "@/shared/api/errors";
import type { components } from "@/shared/api/generated";
import { authKeys } from "@/shared/api/query-keys";
import { clearSession, useSessionStore, type CurrentUser } from "@/shared/auth/session";

export type RegisterInput = components["schemas"]["RegisterRequest"];
export type LoginInput = components["schemas"]["LoginRequest"];
export type ActiveSession = components["schemas"]["ActiveSessionResponse"];

export function useRegister(): UseMutationResult<
  components["schemas"]["RegisterResponse"],
  ApiError,
  RegisterInput
> {
  return useMutation({
    mutationFn: (body: RegisterInput) => unwrap(api.POST("/api/v1/auth/register", { body })),
  });
}

export function useLogin(): UseMutationResult<CurrentUser, ApiError, LoginInput> {
  const setSession = useSessionStore((state) => state.setSession);
  return useMutation({
    mutationFn: async (body: LoginInput) => {
      const payload = await unwrap(api.POST("/api/v1/auth/login", { body }));
      setSession(payload);
      return payload.user;
    },
  });
}

export function useLogout(): UseMutationResult<void, ApiError, void> {
  return useMutation({
    mutationFn: async () => {
      await expectNoContent(api.POST("/api/v1/auth/logout", {}));
    },
    // `onSettled` et non `onSuccess` : que l'API réponde ou non, l'utilisateur a
    // demandé à partir. Le laisser connecté côté client parce que le réseau a
    // hoqueté serait le pire des deux mondes.
    onSettled: () => {
      clearSession();
    },
  });
}

export function useVerifyEmail(): UseMutationResult<void, ApiError, string> {
  return useMutation({
    mutationFn: async (token: string) => {
      await expectNoContent(api.POST("/api/v1/auth/verify-email", { body: { token } }));
    },
  });
}

export function useForgotPassword(): UseMutationResult<void, ApiError, string> {
  return useMutation({
    mutationFn: async (email: string) => {
      await expectNoContent(api.POST("/api/v1/auth/forgot-password", { body: { email } }));
    },
  });
}

export function useResetPassword(): UseMutationResult<
  void,
  ApiError,
  { token: string; new_password: string }
> {
  return useMutation({
    mutationFn: async (body) => {
      await expectNoContent(api.POST("/api/v1/auth/reset-password", { body }));
    },
  });
}

/** Relit le compte en base : c'est `/me` qui dit la vérité, pas le jeton. */
export function useCurrentUser(enabled: boolean): UseQueryResult<CurrentUser, ApiError> {
  return useQuery<CurrentUser, ApiError>({
    queryKey: authKeys.me(),
    queryFn: () => unwrap(api.GET("/api/v1/auth/me")),
    enabled,
  });
}

export function useSessions(): UseQueryResult<ActiveSession[], ApiError> {
  return useQuery<ActiveSession[], ApiError>({
    queryKey: authKeys.sessions(),
    queryFn: async () => (await unwrap(api.GET("/api/v1/auth/sessions"))).data,
  });
}

export function useRevokeSession(): UseMutationResult<void, ApiError, string> {
  return useMutation({
    mutationFn: async (sessionId: string) => {
      await expectNoContent(
        api.DELETE("/api/v1/auth/sessions/{session_id}", {
          params: { path: { session_id: sessionId } },
        }),
      );
    },
  });
}
